/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaConstArrayModel,
	ISchemaConstEnumOptionEntry,
	ISchemaConstMapModel,
	ISchemaConstMapModelEntry,
	TGenericBlueprintSchema,
	TGenericModelNode,
	TSchemaConstEnumValueSchema
} from "@adapptio/lib-blueprint";
import { REFERENCE_TYPE } from "../modules/Core/ReferenceGuide/ReferenceGuideTypes";
import { TInspectorControl } from "./controls";
import { THintType } from "./hints/InspectorHintTypes";

export enum INSPECTOR_NODE_TYPE {
	DEFAULT = "default",
	LIST = "list",
	MAP = "map"
}

type TInspectorNodeResolveFn<TNode extends TInspectorNode> = () => TNode;

type TInspectorChildGetterFn<
	TModel extends TGenericModelNode | ISchemaConstMapModelEntry<TGenericBlueprintSchema>
> = (model: TModel, index: number) => TInspectorNodeResolveFn<TInspectorNode>;

export interface IInspectorNode_Base {
	type: INSPECTOR_NODE_TYPE;
	key: string;
	label: string;
	hintType?: THintType;
	referenceData?: TInspectorNode_ReferenceData;
	descriptor?: any;
	predefineValues?: string[];
	examples?: any;
	required?: boolean;
	default?: any;
	options?: ISchemaConstEnumOptionEntry<TSchemaConstEnumValueSchema>[];
	tooltip: string;
	deps: TGenericModelNode[];
	controls: TInspectorControl[];
}

export interface TInspectorNode_ReferenceData {
	name: string;
	type: REFERENCE_TYPE;
	category?: string;
}

export interface IInspectorNode_Default extends IInspectorNode_Base {
	type: INSPECTOR_NODE_TYPE.DEFAULT;
	children: TInspectorNodeResolveFn<TInspectorNode>[];
	group?: boolean;
	defaultCollapsed?: boolean;
	collapsedControls?: TInspectorControl[];
}

export interface IInspectorNode_List extends IInspectorNode_Base {
	type: INSPECTOR_NODE_TYPE.LIST;
	model: ISchemaConstArrayModel<TGenericBlueprintSchema>;
	getChildren: TInspectorChildGetterFn<TGenericModelNode>;
}

export interface IInspectorNode_Map extends IInspectorNode_Base {
	type: INSPECTOR_NODE_TYPE.MAP;
	model: ISchemaConstMapModel<TGenericBlueprintSchema>;
	getChildren: TInspectorChildGetterFn<ISchemaConstMapModelEntry<TGenericBlueprintSchema>>;
}

export type TInspectorNode = IInspectorNode_Default | IInspectorNode_List | IInspectorNode_Map;

export function isInspectorNodeDefault(value: TInspectorNode): value is IInspectorNode_Default {
	return value?.type === INSPECTOR_NODE_TYPE.DEFAULT;
}

export function isInspectorNodeList(value: TInspectorNode): value is IInspectorNode_List {
	return value?.type === INSPECTOR_NODE_TYPE.LIST;
}

export function isInspectorNodeMap(value: TInspectorNode): value is IInspectorNode_Map {
	return value?.type === INSPECTOR_NODE_TYPE.MAP;
}
